import { House } from '@phosphor-icons/react';
import { Button, Icon, Text } from '@shh/ui';
import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  useHref,
  useLocation,
  useNavigate,
  type NavigateOptions,
} from 'react-router';
import { FamilyProvider, useFamily } from '../data/family';
import { useSession } from '../data/session';
import { CalendarPage } from '../pages/CalendarPage';
import { AccountActionPage } from '../pages/start/AccountActionPage';
import { FreizeitPage } from '../pages/leisure/FreizeitPage';
import { IdeasPage } from '../pages/leisure/IdeasPage';
import { VouchersPage } from '../pages/leisure/VouchersPage';
import { WishesPage } from '../pages/leisure/WishesPage';
import { PersonPage } from '../pages/PersonPage';
import { FeedbackPage } from '../pages/FeedbackPage';
import { SettingsPage } from '../pages/SettingsPage';
import { StarsPage } from '../pages/StarsPage';
import { TasksPage } from '../pages/TasksPage';
import { TodayPage } from '../pages/TodayPage';
import { LoginPage } from '../pages/start/LoginPage';
import { PairPage } from '../pages/start/PairPage';
import { SetupPage } from '../pages/start/SetupPage';
import { NotFoundPage } from './NotFoundPage';
import { AdminPage } from '../pages/AdminPage';
import { Shell } from './Shell';
import { UpdatePrompt } from './UpdatePrompt';
import { appText } from '../i18n/app';

declare module 'react-aria-components' {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

/** After this long something is stuck (offline, lost token) – offer a way out instead of a spinner. */
const SLOW_MS = 15_000;

function Splash() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setSlow(true), SLOW_MS);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <main
      className="flex min-h-dvh flex-col items-center justify-center gap-gap-lg p-gutter text-center"
      aria-busy={!slow}
    >
      <Icon icon={House} size="xl" className="animate-pulse text-text-muted" />
      {slow ? (
        <>
          <Text tone="secondary">{appText.slow}</Text>
          <Button onPress={() => window.location.reload()}>{appText.reload}</Button>
        </>
      ) : (
        <span className="sr-only">{appText.loading}</span>
      )}
    </main>
  );
}

function Root() {
  const navigate = useNavigate();
  return (
    // React Aria links (tab bar, side nav) navigate through React Router – no full page loads.
    <AriaRouterProvider navigate={(to, options) => void navigate(to, options)} useHref={useHref}>
      <Outlet />
      {/* a new page starts at its top; "back" returns to where you were (Mike, 14.10.) */}
      <ScrollRestoration />
      <UpdatePrompt />
    </AriaRouterProvider>
  );
}

/** Waits for the first snapshots so the dashboard never flashes empty states. */
function Loaded() {
  const f = useFamily();
  return f.loaded ? <Shell /> : <Splash />;
}

/**
 * The app decides by sign-in state, not by URL:
 *  - signed out      → login (or the pairing pad on /koppeln, /wand)
 *  - no family yet   → setup wizard; an anonymous device → pairing pad
 *  - ready           → the family shell
 */
function Gate() {
  const s = useSession();
  const path = useLocation().pathname;
  if (s.status === 'loading') return <Splash />;
  if (s.status === 'signed-out')
    return path === '/koppeln' || path === '/wand' ? <PairPage /> : <LoginPage />;
  if (s.status === 'no-household') return s.user.isAnonymous ? <PairPage /> : <SetupPage />;
  return (
    <FamilyProvider key={s.householdId} session={s}>
      <Loaded />
    </FamilyProvider>
  );
}

/** "Essen" loads on its first visit – the list and its catalogue stay out of the main file. */
const FoodPage = lazy(() => import('../pages/FoodPage').then((m) => ({ default: m.FoodPage })));
const WeekPage = lazy(() => import('../pages/WeekPage').then((m) => ({ default: m.WeekPage })));
const RecipesPage = lazy(() =>
  import('../pages/RecipesPage').then((m) => ({ default: m.RecipesPage })),
);
const RecipePage = lazy(() =>
  import('../pages/RecipePage').then((m) => ({ default: m.RecipePage })),
);
const CookPage = lazy(() => import('../pages/CookPage').then((m) => ({ default: m.CookPage })));
const MealIdeasPage = lazy(() =>
  import('../pages/MealIdeasPage').then((m) => ({ default: m.MealIdeasPage })),
);
const lazyPage = (page: ReactNode) => <Suspense fallback={null}>{page}</Suspense>;

/** Routes are German, because families see them in the address bar and in shared links. */
export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        element: <Gate />,
        children: [
          { path: '/', element: <TodayPage /> },
          { path: '/kalender', element: <CalendarPage /> },
          { path: '/aufgaben', element: <TasksPage /> },
          {
            path: '/essen',
            element: (
              <Suspense fallback={null}>
                <FoodPage />
              </Suspense>
            ),
          },
          // Rezepte (1.21): tab, recipe page and cooking view – loaded on their first visit
          { path: '/essen/heute', element: lazyPage(<MealIdeasPage />) },
          { path: '/essen/woche', element: lazyPage(<WeekPage />) },
          { path: '/essen/rezepte', element: lazyPage(<RecipesPage />) },
          { path: '/essen/rezepte/:id', element: lazyPage(<RecipePage />) },
          { path: '/essen/rezepte/:id/kochen', element: lazyPage(<CookPage />) },
          { path: '/sterne', element: <StarsPage /> },
          { path: '/freizeit', element: <FreizeitPage /> },
          { path: '/freizeit/ideen', element: <IdeasPage /> },
          { path: '/freizeit/gutscheine', element: <VouchersPage /> },
          { path: '/freizeit/wuensche/:owner', element: <WishesPage /> },
          { path: '/person/:id', element: <PersonPage /> },
          { path: '/einstellungen', element: <SettingsPage /> },
          { path: '/feedback', element: <FeedbackPage /> },
          // Bookmarks for the kiosk tablet / pairing – once paired they simply show "Heute".
          { path: '/wand', element: <Navigate to="/" replace /> },
          { path: '/koppeln', element: <Navigate to="/" replace /> },
          { path: '/anmelden', element: <Navigate to="/" replace /> },
        ],
      },
      // Links from account mails – reachable signed in and out, so outside the Gate.
      { path: '/konto/aktion', element: <AccountActionPage /> },
      // Testfamilien (1.17): only Mike – the server decides, everybody else sees "not found"
      { path: '/admin', element: <AdminPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
